import React, { useContext } from 'react';
import { connect, useDispatch } from 'react-redux';
import { noop, bemGenerator } from '@prisjakt/ui-base';
import cn from 'classnames';
import Rating from '@prisjakt/ui-rating';
import { AppContext } from '@prisjakt/ui-app-context';
import ProductMedia from '../product-media';
import LinkToPriceHistory from './components/LinkToPriceHistory';
import LinkToReviews from './components/LinkToReviews';
import PriceHistorySummary from '../price-history-summary';
import { headerClickEvent } from '../../actions/product';
import ProductActionButtons from './components/product-actions-buttons';
import SentimentBadge from '../../../../components/sentiment-badge';
import Viewport from '../../../../components/viewport';
import ErrorBoundary from '../../../../components/error-boundary';

import { ProductHeaderProps } from './types';

import * as S from './styles-desktop';
import * as ProductStyles from '../../styles';
import './index.scss';

const ProductHeader = ({
  className,
  title,
  product,
  currentCampaign,
  innerRef,
  showPriceHistory,
  categoryTopProducts,
}: ProductHeaderProps) => {
  const { t, linkTo } = useContext(AppContext);
  const dispatch = useDispatch();

  if (!product) {
    return null;
  }

  const headerStyle = { minWidth: '100%' };

  const styles = bemGenerator('product-header', [
    'media',
    'content',
    'title',
    'label',
  ]);

  const getDisplayProperties = (nodes: any) =>
    nodes.map((node: any) => node.prettyVerbose).filter((item: any) => item);

  const displayProperties =
    product && product.coreProperties
      ? getDisplayProperties(product.coreProperties.nodes)
      : [];

  const handleRankCategoryClick = () =>
    dispatch(headerClickEvent('rank-category'));

  const handlePriceHistoryClick = () =>
    dispatch(headerClickEvent('price-history'));

  const renderProductSummary = () => {
    if (!product) return null;
    const isLoading = !product.statistics;
    const hasPriceHistoryData =
      product.statistics &&
      product.statistics.nodes &&
      product.statistics.nodes.length > 0 &&
      product.statistics.pageInfo &&
      product.statistics.pageInfo.lowestPrice;
    const hasPriceHistory =
      Boolean(
        product && product.priceForecast && product.priceForecast.length
      ) ||
      Boolean(
        product &&
          product.statistics &&
          product.statistics.nodes &&
          product.statistics.nodes.length > 1
      );

    return (
      <S.ProductSummary>
        {showPriceHistory && (isLoading || hasPriceHistoryData) && (
          <LinkToPriceHistory
            product={product}
            onClick={handlePriceHistoryClick}
          >
            {(isLoading || hasPriceHistory) && (
              <PriceHistorySummary product={product} isLoading={isLoading} />
            )}
          </LinkToPriceHistory>
        )}
        <S.ProductActions>
          <ProductActionButtons product={product} horizontal isDesktop />
        </S.ProductActions>
      </S.ProductSummary>
    );
  };

  const renderRankContainer = () => {
    if (!product) return null;

    const compareUrl = linkTo('productCompare', {
      category: product.category,
      products: categoryTopProducts,
    });
    const showTopTenCompareLink =
      categoryTopProducts && categoryTopProducts.length;

    return (
      <S.RankContainer>
        {product.category &&
          product.popularity &&
          Boolean(product.popularity.inCategory) && (
            <>
              <S.CategoryRank>
                {t('product', 'rank.in-category', {
                  categoryRank: (
                    <S.CategoryRankPill key="categoryRank">
                      {t('product', 'rank.position', {
                        rank: (
                          <S.CategoryRankNumber key="rank">
                            {product.popularity.inCategory}
                          </S.CategoryRankNumber>
                        ),
                      })}
                    </S.CategoryRankPill>
                  ),
                  categoryLink: (
                    <S.Link
                      to={product.category.pathName}
                      key="categoryLink"
                      onClick={handleRankCategoryClick}
                    >
                      {product.category.name}
                    </S.Link>
                  ),
                })}
              </S.CategoryRank>
              {showTopTenCompareLink && <span>|</span>}
            </>
          )}
        {showTopTenCompareLink && (
          <S.Link to={compareUrl} rel="nofollow">
            {t('product', 'product-compare.top-ten-link')}
          </S.Link>
        )}
      </S.RankContainer>
    );
  };

  return (
    <header className={cn(styles.block, className)} ref={innerRef}>
      <Viewport tablet style={headerStyle}>
        <S.Title outer data-test="ProductTitle">
          {title}
        </S.Title>
      </Viewport>
      <div className={styles.media}>
        <ErrorBoundary onError={noop}>
          {currentCampaign ? (
            <S.CampaignBadge campaign={currentCampaign} size="large" />
          ) : null}
        </ErrorBoundary>
        <ErrorBoundary onError={noop}>
          {product.media && (
            <ProductMedia
              name={product.name}
              media={product.media}
              category={product.category}
            />
          )}
        </ErrorBoundary>
      </div>
      <div className={styles.content}>
        <Viewport desktop>
          <S.Title inner data-test="ProductTitle">
            {title}
          </S.Title>
        </Viewport>
        <ErrorBoundary onError={noop}>
          <>
            <S.More>
              <S.RatingWrapper>
                <LinkToReviews product={product}>
                  {product.userReviewSummary &&
                  product.userReviewSummary.count !== 0 ? (
                    <ProductStyles.Rating>
                      <Rating
                        hide={product.userReviewSummary.count === 0}
                        rate={product.userReviewSummary.rating}
                        count={product.userReviewSummary.count}
                        size="medium"
                        showRate
                        showRateAs="number"
                      />
                    </ProductStyles.Rating>
                  ) : (
                    <ProductStyles.WriteReview>
                      {t('reviews', 'button.write-review')}
                    </ProductStyles.WriteReview>
                  )}
                </LinkToReviews>
              </S.RatingWrapper>
              {Boolean(displayProperties.length) && (
                <S.DisplayProperties
                  toneDown
                  size="large"
                  maxrows={3}
                  properties={displayProperties
                    // Cut off at max three core properties
                    .slice(0, 3)}
                  showAsList
                />
              )}
              {product.reviewBadges && product.reviewBadges.length > 0 ? (
                <S.ReviewBadges>
                  {product.reviewBadges.slice(0, 3).map((badge: any) => (
                    <SentimentBadge
                      badge={badge}
                      key={badge.id}
                      showNrOfReviewsAs="text"
                    />
                  ))}
                </S.ReviewBadges>
              ) : (
                ''
              )}
              {renderRankContainer()}
            </S.More>
          </>
        </ErrorBoundary>
      </div>
      {renderProductSummary()}

      {product.brand && product.brand.featured && (
        <S.StoreLogo
          title={t('product', 'store-info.more-about-store', {
            store_name: product.brand.name,
          })}
          to={product.brand.pathName}
        >
          {product.brand.featured && product.brand.logo ? (
            <img src={product.brand.logo} alt={product.brand.name} />
          ) : (
            product.brand.name
          )}
        </S.StoreLogo>
      )}
    </header>
  );
};

export default connect()(ProductHeader);
